<template>
    <div class="tour-media-gallery">
        <div class="gallery-header">
            <h3>Tour Media</h3>
            
            <div class="gallery-controls">
                <div class="filter-tabs">
                    <button 
                        v-for="filter in filters" 
                        :key="filter.value"
                        :class="['filter-tab', { active: activeFilter === filter.value }]"
                        @click="activeFilter = filter.value"
                    >
                        {{ filter.label }}
                        <span class="count">{{ getFilterCount(filter.value) }}</span>
                    </button>
                </div>

                <themed-select
                    v-model="selectedCompFilter"
                    :options="compFilterOptions"
                    placeholder="All Properties"
                    class="comp-filter"
                />

                <themed-button @click="$emit('upload')">
                    <theme-icon icon="plus" type="icons" />
                    Add Media
                </themed-button>
            </div>
        </div>

        <div v-if="loading" class="gallery-loading">
            <div class="spinner"></div>
            <p>Loading media...</p>
        </div>

        <div v-else-if="filteredMedia.length === 0" class="gallery-empty">
            <theme-icon icon="image" type="icons" />
            <p>No media found</p>
            <themed-button @click="$emit('upload')">
                Upload Media
            </themed-button>
        </div>

        <div v-else class="gallery-grid">
            <!-- Photos -->
            <div 
                v-for="item in filteredMedia" 
                :key="item.id"
                :class="['gallery-item', `type-${item.type}`]"
                @click="openViewer(item)"
            >
                <div class="item-preview">
                    <img 
                        v-if="item.type === 'photo'" 
                        :src="item.thumbnail_url || item.file_url" 
                        :alt="item.caption || 'Photo'"
                        loading="lazy"
                    />
                    <div v-else-if="item.type === 'video'" class="video-thumbnail">
                        <theme-icon icon="video" type="icons" />
                        <span>Video</span>
                    </div>
                    <div v-else-if="item.type === 'note'" class="note-thumbnail">
                        <theme-icon icon="note" type="icons" />
                        <span>Note</span>
                    </div>
                </div>

                <div class="item-info">
                    <div class="item-caption">
                        {{ item.caption || 'No caption' }}
                    </div>
                    <div class="item-meta">
                        <span class="author">{{ getAuthorName(item) }}</span>
                        <span class="date">{{ formatDate(item.captured_at || item.created_at) }}</span>
                    </div>
                    <div v-if="item.tour_comp" class="item-comp">
                        <theme-icon icon="building" type="icons" />
                        {{ getCompName(item.tour_comp) }}
                    </div>
                </div>

                <div class="item-actions">
                    <button 
                        v-if="canEdit(item)"
                        @click.stop="editItem(item)" 
                        class="action-btn"
                        title="Edit"
                    >
                        <theme-icon icon="edit" type="icons" />
                    </button>
                    <button 
                        v-if="canDelete(item)"
                        @click.stop="deleteItem(item)" 
                        class="action-btn delete"
                        title="Delete"
                    >
                        <theme-icon icon="trash" type="icons" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Media Viewer Modal -->
        <tour-media-viewer
            v-if="viewerItem"
            :item="viewerItem"
            :tour-id="tourId"
            @close="viewerItem = null"
            @updated="handleMediaUpdated"
            @deleted="handleMediaDeleted"
        />
    </div>
</template>

<script>
import ThemedButton from '../ThemedButton.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemeIcon from '../ThemeIcon.vue';
import TourMediaViewer from './TourMediaViewer.vue';
import moment from 'moment';

export default {
    name: 'TourMediaGallery',

    components: {
        ThemedButton,
        ThemedSelect,
        ThemeIcon,
        TourMediaViewer,
    },

    props: {
        tourId: {
            type: Number,
            required: true,
        },
        comps: {
            type: Array,
            default: () => [],
        },
        tourCreatorId: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            media: [],
            loading: false,
            activeFilter: 'all',
            selectedCompFilter: null,
            viewerItem: null,
            filters: [
                { value: 'all', label: 'All' },
                { value: 'photo', label: 'Photos' },
                { value: 'video', label: 'Videos' },
                { value: 'note', label: 'Notes' },
            ],
        };
    },

    computed: {
        filteredMedia() {
            let filtered = this.media;

            // Filter by type
            if (this.activeFilter !== 'all') {
                filtered = filtered.filter(item => item.type === this.activeFilter);
            }

            // Filter by comp
            if (this.selectedCompFilter) {
                filtered = filtered.filter(item => 
                    item.tour_comp_id === this.selectedCompFilter
                );
            }

            return filtered;
        },

        compFilterOptions() {
            return [
                { value: null, label: 'All Properties' },
                { value: 'general', label: 'General (no property)' },
                ...this.comps.map(comp => ({
                    value: comp.id,
                    label: comp.comp?.building?.name || comp.building?.name || 'Unknown Property',
                })),
            ];
        },
    },

    mounted() {
        this.loadMedia();
    },

    methods: {
        async loadMedia() {
            this.loading = true;
            try {
                const response = await axios.get(`/tours/${this.tourId}/media`);
                this.media = response.data;
            } catch (error) {
                console.error('Failed to load media:', error);
                this.$notify({
                    type: 'error',
                    title: 'Error',
                    text: 'Failed to load media',
                });
            } finally {
                this.loading = false;
            }
        },

        getFilterCount(filterValue) {
            if (filterValue === 'all') {
                return this.media.length;
            }
            return this.media.filter(item => item.type === filterValue).length;
        },

        openViewer(item) {
            this.viewerItem = item;
        },

        editItem(item) {
            this.viewerItem = item;
        },

        async deleteItem(item) {
            if (!confirm('Are you sure you want to delete this media?')) {
                return;
            }

            try {
                await axios.delete(`/tours/${this.tourId}/media/${item.id}`);
                this.media = this.media.filter(m => m.id !== item.id);
                this.$notify({
                    type: 'success',
                    title: 'Deleted',
                    text: 'Media deleted successfully',
                });
            } catch (error) {
                console.error('Failed to delete media:', error);
                this.$notify({
                    type: 'error',
                    title: 'Error',
                    text: 'Failed to delete media',
                });
            }
        },

        handleMediaUpdated(updatedItem) {
            const index = this.media.findIndex(m => m.id === updatedItem.id);
            if (index !== -1) {
                this.$set(this.media, index, updatedItem);
            }
        },

        handleMediaDeleted(deletedId) {
            this.media = this.media.filter(m => m.id !== deletedId);
            this.viewerItem = null;
        },

        canEdit(item) {
            return item.user_id === window.userId;
        },

        canDelete(item) {
            return item.user_id === window.userId || this.tourCreatorId === window.userId;
        },

        getAuthorName(item) {
            if (item.user) {
                return item.user.name;
            }
            if (item.attendee) {
                return item.attendee.attendee_name || item.attendee.attendee_email;
            }
            return 'Unknown';
        },

        getCompName(tourComp) {
            return tourComp.comp?.building?.name || tourComp.building?.name || 'Unknown Property';
        },

        formatDate(date) {
            return moment(date).format('MMM D, YYYY h:mm A');
        },

        refresh() {
            this.loadMedia();
        },
    },
};
</script>
